import React, { useState, useEffect, useRef } from "react";
import { sendEmailQueue } from "../API/emailAPI";
import { getClients } from "../API/clientAPI";
import {
  listPromotionTemplates,
  savePromotionTemplate,
  deletePromotionTemplate,
  getPromotionImageUploadUrl,
  getPromotionAttachmentUploadUrl,
  deletePromotionImage,
  deletePromotionAttachment,
} from "../API/promotionAPI";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import ImageResize from "quill-image-resize";

import {
  Button,
  Typography,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  CardHeader,
  TextField,
  FormControlLabel,
  Checkbox,
  LinearProgress,
  Alert,
  Stack,
} from "@mui/material";
import ImageIcon from "@mui/icons-material/Image";
import CampaignIcon from "@mui/icons-material/Campaign";
import BaseWrapper from "../../components/shared/BaseWrapper";

Quill.register("modules/imageResize", ImageResize);

const Font = Quill.import("formats/font");
Font.whitelist = ["serif", "sans-serif", "monospace"];
Quill.register(Font, true);

const Size = Quill.import("formats/size");
Size.whitelist = ["small", "normal", "large", "huge"];
Quill.register(Size, true);

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------
const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const resizeImage = (file) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement("canvas");
    const reader = new FileReader();

    reader.onload = (e) => {
      img.src = e.target.result;
      img.onload = () => {
        canvas.width = 200;
        canvas.height = 200;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, 200, 200);
        canvas.toBlob(
          (blob) => {
            if (blob) resolve(blob);
            else reject(new Error("Image resizing failed"));
          },
          file.type
        );
      };
    };

    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

const uploadFileToPresignedUrl = async ({ uploadUrl, file, contentType }) => {
  const response = await fetch(uploadUrl, {
    method: "PUT",
    headers: {
      "Content-Type": contentType,
    },
    body: file,
  });

  if (!response.ok) {
    throw new Error(`Upload failed with status ${response.status}`);
  }
};

const buildPromotionEmailHtml = ({ body, attachments = [] }) => {
  const attachmentLinks = attachments
    .map((att) => {
      const safeName = att.fileName || "Attachment";
      return `<li><a href="${att.fileUrl}" target="_blank" rel="noopener noreferrer">${safeName}</a></li>`;
    })
    .join("");

  return `
    <html>
      <body>
        ${body}
        ${attachmentLinks ? `<p>Attachments:</p><ul>${attachmentLinks}</ul>` : ""}
        <hr />
        <footer style="margin-top: 20px; font-size: 12px; color: #666;">
          <p>Thank you for being with us!</p>
          <p>If you no longer wish to receive these emails, you can <a href="mailto:unsubscribe-boaz@maestrosystemsinc.com">unsubscribe here</a>.</p>
          <p>Our Address:</p>
          <p>8898 Sampaguita cor. P. Victor Sts., Guadlupe Nuevo, Makati City</p>
          <p>2 Mistletoe cor Mayflower Sts., Del Nacia 3, Talipapa, Quezon City</p>
        </footer>
      </body>
    </html>
  `;
};

const normalizeValue = (value) => (value ?? "").toString().trim().toLowerCase();

const filterClientsByPromotionFilter = ({ clients, clientFilter }) => {
  const selectedLocation = normalizeValue(clientFilter.location_type);
  const selectedCategory = normalizeValue(clientFilter.category);
  const selectedServices = new Set(
    (clientFilter.service_types || []).map(normalizeValue)
  );

  const wantsAllLocation = selectedLocation === "all";
  const wantsAllCategory = selectedCategory === "all";
  const wantsAllServices =
    selectedServices.size === 0 || selectedServices.size === 4;

  return (clients || []).filter((client) => {
    const clientLocation = normalizeValue(client.location_type);
    const clientCategory = normalizeValue(client.category);
    const clientServices = new Set(
      (client.service_types || []).map(normalizeValue)
    );

    const locationOk = wantsAllLocation || clientLocation === selectedLocation;
    const categoryOk = wantsAllCategory || clientCategory === selectedCategory;
    const serviceOk = wantsAllServices
      ? true
      : [...selectedServices].some((service) => clientServices.has(service));

    return locationOk && categoryOk && serviceOk;
  });
};

const getSelectableClientContacts = (clients = []) => {
  const rows = [];
  const seen = new Set();

  clients.forEach((client) => {
    const clientName =
      client.name ||
      client.client_name ||
      `${client.firstname || ""} ${client.lastname || ""}`.trim() ||
      "(no name)";

    (client.contacts || []).forEach((contact) => {
      if (Array.isArray(contact.emails)) {
        contact.emails.forEach((entry) => {
          const email =
            typeof entry === "string"
              ? entry.trim()
              : entry?.email?.trim();

          if (!email) return;

          const key = email.toLowerCase();
          if (seen.has(key)) return;

          seen.add(key);
          rows.push({ name: clientName, email });
        });
      }

      if (contact.email) {
        const email = contact.email.trim();
        const key = email.toLowerCase();

        if (!email || seen.has(key)) return;

        seen.add(key);
        rows.push({ name: clientName, email });
      }
    });

    // optional fallback if some old records still use client.email
    if (client.email) {
      const email = client.email.trim();
      const key = email.toLowerCase();

      if (email && !seen.has(key)) {
        seen.add(key);
        rows.push({ name: clientName, email });
      }
    }
  });

  return rows;
};

const SendPromotions = () => {
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [clientsError, setClientsError] = useState("");

  const [recipients, setRecipients] = useState([]);
  const [customEmails, setCustomEmails] = useState("");
  const [customEmailList, setCustomEmailList] = useState([]);

  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");

  const [attachments, setAttachments] = useState([]);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [previewBody, setPreviewBody] = useState("");

  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [dryRun, setDryRun] = useState(false);

  const quillRef = useRef(null);
  const imageInputRef = useRef(null);
  const attachmentInputRef = useRef(null);

  const [client_filter, setClientFilter] = useState({
    category: "all",
    service_types: [
      "general_pest_control",
      "termite_control",
      "qpms",
      "disinfection",
    ],
    location_type: "all",
  });

  const service_types = [
    "General Pest Control",
    "Termite Control",
    "QPMS",
    "Disinfection",
  ];

  const locationTypeOptions = [
    "Bank",
    "Bar",
    "BPO",
    "Commercial Complex",
    "Commissary",
    "Condo Unit",
    "Condominium",
    "Department Store",
    "Embassy",
    "Food Manufacturing",
    "Government Office",
    "Hospital or Clinic",
    "Hotel",
    "Industrial Plant",
    "Mall",
    "Manufacturing",
    "Office",
    "Parks and Parking",
    "Residential (Apartment)",
    "Residential (Single Unit)",
    "Restaurant or Café",
    "School",
    "Sports Center",
    "Supermarket",
    "Vehicle",
    "Village",
    "Warehouse",
    "Wet Market",
    "Other",
  ];

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    await Promise.all([fetchClients(), loadTemplates()]);
  };

  const fetchClients = async () => {
    try {
      setClientsLoading(true);
      setClientsError("");
      const clientsList = await getClients();
      setClients(clientsList || []);
    } catch (error) {
      console.error("Error fetching clients:", error);
      setClientsError("Failed to load clients.");
    } finally {
      setClientsLoading(false);
    }
  };

  const retryFetchClients = async () => {
    await fetchClients();
  };

  const handleCategoryChange = (value) => {
    setClientFilter((prev) => ({
      ...prev,
      category: value,
    }));
  };

  const handleServiceTypeChange = (e, formattedOption) => {
    setClientFilter((prev) => {
      const isChecked = e.target.checked;

      const updatedServiceTypes = isChecked
        ? [...prev.service_types, formattedOption]
        : prev.service_types.filter((option) => option !== formattedOption);

      return {
        ...prev,
        service_types: updatedServiceTypes,
      };
    });
  };

  const handleLocationTypeChange = (value) => {
    setClientFilter((prev) => ({
      ...prev,
      location_type: value,
    }));
  };

  const loadTemplates = async () => {
    try {
      const loadedTemplates = await listPromotionTemplates();
      setTemplates(Array.isArray(loadedTemplates) ? loadedTemplates : []);
    } catch (error) {
      console.error("Error loading templates:", error);
      setErrorMessage(error.message || "Failed to load templates.");
    }
  };

  const handleToggleRecipient = (email) => {
    const normalizedEmail = email.toLowerCase();

    setRecipients((prev) =>
      prev.includes(normalizedEmail)
        ? prev.filter((recipient) => recipient !== normalizedEmail)
        : [...prev, normalizedEmail]
    );
  };

  const handleSelectAll = () => {
    setRecipients(selectableContacts.map((contact) => contact.email.toLowerCase()));
  };

  const handleDeselectAll = () => {
    setRecipients([]);
  };

  const handleCustomEmailChange = (event) => {
    setCustomEmails(event.target.value);
  };

  const handleAddCustomEmails = () => {
    const customEmailArray = customEmails
      .split(",")
      .map((email) => email.trim().toLowerCase())
      .filter((email) => validateEmail(email));

    if (customEmailArray.length === 0) {
      setErrorMessage("No valid email addresses found.");
      return;
    }

    setRecipients((prev) => [...new Set([...prev, ...customEmailArray])]);
    setCustomEmailList((prev) => [...new Set([...prev, ...customEmailArray])]);
    setCustomEmails("");
    setSuccessMessage("Custom emails added successfully.");
  };

  const handleDeleteCustomEmail = (emailToDelete) => {
    setCustomEmailList((prev) => prev.filter((email) => email !== emailToDelete));
    setRecipients((prev) => prev.filter((email) => email !== emailToDelete));
  };

  const saveTemplateHandler = async () => {
    try {
      setErrorMessage("");
      setSuccessMessage("");

      const newTemplate = {
        id: Date.now(),
        name: subject || "Untitled Template",
        recipients,
        customEmails: customEmailList,
        subject,
        body,
        attachments,
        uploadedImages,
      };

      await savePromotionTemplate(newTemplate);
      await loadTemplates();
      setSuccessMessage("Template saved successfully.");
    } catch (error) {
      console.error("Error saving template:", error);
      setErrorMessage(error.message || "Failed to save template.");
    }
  };

  const loadTemplate = (templateId) => {
    const template = templates.find(
      (tmpl) => String(tmpl.id) === String(templateId)
    );

    if (!template) {
      setSuccessMessage("");
      setErrorMessage("Template not found.");
      return;
    }

    setRecipients(template.recipients || []);
    setCustomEmailList(template.customEmails || []);
    setSubject(template.subject || "");
    setBody(template.body || "");
    setAttachments(template.attachments || []);
    setUploadedImages(template.uploadedImages || []);
    setSelectedTemplate(String(template.id));
    setErrorMessage("");
    setSuccessMessage("Template loaded successfully.");
  };

  const deleteTemplateHandler = async (templateId) => {
    if (!templateId) {
      setSuccessMessage("");
      setErrorMessage("No template selected.");
      return;
    }

    try {
      setErrorMessage("");
      setSuccessMessage("");

      await deletePromotionTemplate(templateId);
      await loadTemplates();

      if (String(selectedTemplate) === String(templateId)) {
        setSelectedTemplate("");
      }

      setSuccessMessage("Template deleted successfully.");
    } catch (error) {
      console.error("Error deleting template:", error);
      setErrorMessage(error.message || "Failed to delete template.");
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsLoading(true);
      setErrorMessage("");
      setSuccessMessage("");

      const resizedBlob = await resizeImage(file);
      const resizedFile = new File([resizedBlob], file.name, {
        type: file.type,
      });

      const { uploadUrl, fileUrl, key } = await getPromotionImageUploadUrl({
        fileName: resizedFile.name,
        contentType: resizedFile.type,
      });

      await uploadFileToPresignedUrl({
        uploadUrl,
        file: resizedFile,
        contentType: resizedFile.type,
      });

      setBody((prevBody) =>
        `${prevBody}<img src="${fileUrl}" alt="Embedded Image" style="width:200px;height:200px;" />`
      );

      setUploadedImages((prev) => [
        ...prev,
        {
          fileName: file.name,
          originalSize: file.size,
          compressedSize: resizedBlob.size,
          url: fileUrl,
          key,
        },
      ]);

      setSuccessMessage(`${file.name} uploaded successfully.`);
    } catch (error) {
      console.error("Error during image upload:", error);
      setErrorMessage(error.message || "Image upload failed.");
    } finally {
      setIsLoading(false);
      if (imageInputRef.current) {
        imageInputRef.current.value = "";
      }
    }
  };

  const handleAttachmentChange = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    try {
      setIsLoading(true);
      setErrorMessage("");
      setSuccessMessage("");

      const uploadedFiles = [];

      for (const file of files) {
        const { uploadUrl, fileUrl, key } =
          await getPromotionAttachmentUploadUrl({
            fileName: file.name,
            contentType: file.type || "application/octet-stream",
          });

        await uploadFileToPresignedUrl({
          uploadUrl,
          file,
          contentType: file.type || "application/octet-stream",
        });

        uploadedFiles.push({
          fileName: file.name,
          fileUrl,
          fileSize: file.size,
          key,
        });
      }

      setAttachments((prev) => [...prev, ...uploadedFiles]);
      setSuccessMessage("Attachment(s) uploaded successfully.");
    } catch (error) {
      console.error("Error uploading attachments:", error);
      setErrorMessage(error.message || "Attachment upload failed.");
    } finally {
      setIsLoading(false);
      if (attachmentInputRef.current) {
        attachmentInputRef.current.value = "";
      }
    }
  };

  const handleDeleteImage = async (key) => {
    try {
      setErrorMessage("");
      setSuccessMessage("");
      await deletePromotionImage({ key });
      setUploadedImages((prev) => prev.filter((image) => image.key !== key));
      setSuccessMessage("Image deleted successfully.");
    } catch (error) {
      console.error("Error deleting image:", error);
      setErrorMessage(error.message || "Failed to delete image.");
    }
  };

  const handleDeleteAttachment = async (key) => {
    try {
      setErrorMessage("");
      setSuccessMessage("");
      await deletePromotionAttachment({ key });
      setAttachments((prev) =>
        prev.filter((attachment) => attachment.key !== key)
      );
      setSuccessMessage("Attachment deleted successfully.");
    } catch (error) {
      console.error("Error deleting attachment:", error);
      setErrorMessage(error.message || "Failed to delete attachment.");
    }
  };

  const previewEmail = () => {
    setErrorMessage("");
    setSuccessMessage("");
    setPreviewBody(buildPromotionEmailHtml({ body, attachments }));
  };

  const handleSendEmail = async () => {
    try {
      setIsLoading(true);
      setErrorMessage("");
      setSuccessMessage("");

      const filteredClientEmails = selectableContacts.map((contact) =>
        contact.email.toLowerCase()
      );

      const allRecipients = [
        ...new Set([
          ...filteredClientEmails,
          ...recipients.map((email) => email.toLowerCase()),
          ...customEmailList.map((email) => email.toLowerCase()),
        ]),
      ];

      const htmlBody = buildPromotionEmailHtml({ body, attachments });

      if (!allRecipients.length) {
        setErrorMessage(
          "No recipients matched your current filters. Adjust Category, Location Type, or Service Type."
        );
        return;
      }

      if (!subject || !body) {
        setErrorMessage("Subject and email body are required.");
        return;
      }

      const lambdaPayload = {
        from: "boaz-promotions@maestrosystemsinc.com",
        recipients: allRecipients,
        subject,
        body: htmlBody,
        dryRun,
      };

      await sendEmailQueue(lambdaPayload);
      setSuccessMessage("Email sent successfully.");
    } catch (error) {
      console.error("Error sending email:", error);
      setErrorMessage(error.message || "Failed to send email.");
    } finally {
      setIsLoading(false);
    }
  };

  const modules = {
    toolbar: [
      [{ font: Font.whitelist }],
      [{ size: Size.whitelist }],
      [{ header: "1" }, { header: "2" }],
      [{ list: "ordered" }, { list: "bullet" }],
      ["bold", "italic", "underline"],
      [{ align: [] }],
      ["link"],
      ["clean"],
    ],
    imageResize: {},
  };

  const editorSx = {
    "& .ql-container": { minHeight: 260 },
    "& .ql-editor": { minHeight: 220, fontSize: 16, paddingBottom: "24px" },
  };

  const filteredClients = filterClientsByPromotionFilter({
    clients,
    clientFilter: client_filter,
  });

  const selectableContacts = getSelectableClientContacts(filteredClients);

  return (
    <BaseWrapper>
      <Box sx={{ p: 2 }}>
        <CardHeader
          avatar={<CampaignIcon color="primary" />}
          title="Send Promotions"
          subheader="Compose and send promotional emails safely through backend APIs."
        />

        <Stack spacing={2}>
          {isLoading && <LinearProgress />}
          {!!successMessage && <Alert severity="success">{successMessage}</Alert>}
          {!!errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          {!!clientsError && <Alert severity="warning">{clientsError}</Alert>}

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Load Template</InputLabel>
                <Select
                  value={selectedTemplate}
                  label="Load Template"
                  onChange={(e) => loadTemplate(e.target.value)}
                >
                  <MenuItem value="">Select a template</MenuItem>
                  {templates.map((template) => (
                    <MenuItem key={template.id} value={template.id}>
                      {template.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md="auto">
              <Button
                variant="outlined"
                color="error"
                onClick={() => deleteTemplateHandler(selectedTemplate)}
                disabled={!selectedTemplate || isLoading}
              >
                Delete Template
              </Button>
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel id="category-label">Category</InputLabel>
                <Select
                  labelId="category-label"
                  value={client_filter.category}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  label="Category"
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="VVIP">VVIP</MenuItem>
                  <MenuItem value="VIP">VIP</MenuItem>
                  <MenuItem value="Gold">Gold</MenuItem>
                  <MenuItem value="Silver">Silver</MenuItem>
                  <MenuItem value="Bronze">Bronze</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel id="location-type-label">Location Type</InputLabel>
                <Select
                  labelId="location-type-label"
                  value={client_filter.location_type}
                  onChange={(e) => handleLocationTypeChange(e.target.value)}
                  label="Location Type"
                >
                  <MenuItem value="all">All</MenuItem>
                  {locationTypeOptions.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body2" sx={{ pb: 1, color: "text.secondary" }}>
                Service Type
              </Typography>

              <Stack direction="row" flexWrap="wrap" gap={1}>
                {service_types.map((option) => {
                  const formattedOption = option.toLowerCase().replace(/\s+/g, "_");

                  return (
                    <FormControlLabel
                      key={formattedOption}
                      control={
                        <Checkbox
                          name={formattedOption}
                          checked={client_filter.service_types.includes(formattedOption)}
                          onChange={(e) => handleServiceTypeChange(e, formattedOption)}
                        />
                      }
                      label={option}
                    />
                  );
                })}
              </Stack>
            </Grid>
          </Grid>

          <Box>
            <Typography variant="h6" gutterBottom>
              Recipients
            </Typography>

            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <Button variant="outlined" onClick={handleSelectAll} disabled={clientsLoading}>
                Select All
              </Button>
              <Button variant="outlined" onClick={handleDeselectAll}>
                Deselect All
              </Button>
              <Button variant="text" onClick={retryFetchClients} disabled={clientsLoading}>
                Reload Clients
              </Button>
            </Stack>

            <Box
              sx={{
                maxHeight: 220,
                overflowY: "auto",
                border: "1px solid #ddd",
                borderRadius: 1,
                p: 2,
              }}
            >
              {clientsLoading ? (
                <Typography>Loading clients...</Typography>
              ) : selectableContacts.length === 0 ? (
                <Typography color="text.secondary">
                  No client contacts match the current filters.
                </Typography>
              ) : (
                selectableContacts.map((contact) => (
                  <FormControlLabel
                    key={contact.email}
                    control={
                      <Checkbox
                        checked={recipients.includes(contact.email.toLowerCase())}
                        onChange={() => handleToggleRecipient(contact.email.toLowerCase())}
                      />
                    }
                    label={`${contact.name} - ${contact.email}`}
                  />
                ))
              )}
            </Box>

            <TextField
              label="Selected Recipients"
              fullWidth
              multiline
              minRows={3}
              value={recipients.join(", ")}
              InputProps={{ readOnly: true }}
              sx={{ mt: 2 }}
            />

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={8}>
                <TextField
                  label="Custom Emails (comma separated)"
                  fullWidth
                  value={customEmails}
                  onChange={handleCustomEmailChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Button fullWidth variant="contained" onClick={handleAddCustomEmails}>
                  Add Custom Emails
                </Button>
              </Grid>
            </Grid>

            {!!customEmailList.length && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1">Custom Emails</Typography>
                <Stack spacing={1}>
                  {customEmailList.map((email) => (
                    <Box
                      key={email}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        border: "1px solid #eee",
                        borderRadius: 1,
                        p: 1,
                      }}
                    >
                      <Typography>{email}</Typography>
                      <Button color="error" onClick={() => handleDeleteCustomEmail(email)}>
                        Delete
                      </Button>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
          </Box>

          <TextField
            label="Subject"
            fullWidth
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />

          <Box sx={editorSx}>
            <Typography variant="h6" gutterBottom>
              Body
            </Typography>
            <ReactQuill
              ref={quillRef}
              theme="snow"
              value={body}
              onChange={setBody}
              modules={modules}
            />
          </Box>

          <Box>
            <Typography variant="h6" gutterBottom>
              Upload Image
            </Typography>
            <Button
              component="label"
              startIcon={<ImageIcon />}
              variant="outlined"
              disabled={isLoading}
            >
              Choose Image
              <input
                hidden
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                ref={imageInputRef}
              />
            </Button>

            {!!uploadedImages.length && (
              <Stack spacing={1} sx={{ mt: 2 }}>
                {uploadedImages.map((image) => (
                  <Box
                    key={image.key}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      border: "1px solid #eee",
                      borderRadius: 1,
                      p: 1,
                    }}
                  >
                    <a
                      href={image.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {image.fileName}
                    </a>
                    <Button color="error" onClick={() => handleDeleteImage(image.key)}>
                      Delete
                    </Button>
                  </Box>
                ))}
              </Stack>
            )}
          </Box>

          <Box>
            <Typography variant="h6" gutterBottom>
              Attach Files
            </Typography>
            <Button component="label" variant="outlined" disabled={isLoading}>
              Choose Attachment(s)
              <input
                hidden
                type="file"
                multiple
                onChange={handleAttachmentChange}
                ref={attachmentInputRef}
              />
            </Button>

            {!!attachments.length && (
              <Stack spacing={1} sx={{ mt: 2 }}>
                {attachments.map((attachment) => (
                  <Box
                    key={attachment.key}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      border: "1px solid #eee",
                      borderRadius: 1,
                      p: 1,
                    }}
                  >
                    <a
                      href={attachment.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {attachment.fileName} ({(attachment.fileSize / 1024).toFixed(2)} KB)
                    </a>
                    <Button
                      color="error"
                      onClick={() => handleDeleteAttachment(attachment.key)}
                    >
                      Delete
                    </Button>
                  </Box>
                ))}
              </Stack>
            )}
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={dryRun}
                onChange={(e) => setDryRun(e.target.checked)}
              />
            }
            label="Dry run"
          />

          <Stack direction="row" spacing={2}>
            <Button variant="outlined" onClick={saveTemplateHandler} disabled={isLoading}>
              Save as Template
            </Button>
            <Button variant="contained" onClick={handleSendEmail} disabled={isLoading}>
              Send Email
            </Button>
            <Button variant="text" onClick={previewEmail}>
              Preview Email
            </Button>
          </Stack>

          <Box>
            <Typography variant="h6" gutterBottom>
              Email Preview
            </Typography>
            <Box
              sx={{
                border: "1px solid #ddd",
                borderRadius: 1,
                p: 2,
                minHeight: 120,
                backgroundColor: "#fff",
              }}
              dangerouslySetInnerHTML={{ __html: previewBody }}
            />
          </Box>
        </Stack>
      </Box>
    </BaseWrapper>
  );
};

export default SendPromotions;